import React, { useState, useEffect, useCallback } from 'react';
import { roleCoversAPI } from '../services/api';
import { FiAlertTriangle, FiX, FiUserCheck } from 'react-icons/fi';

// Growth Access → Stand-ins (brainstorms/2026-10-29-stand-in-access-and-several-cmos.md): the Chairman,
// CEO or COO let someone cover the CMO or CSM for set dates. The stand-in keeps their own access and
// also gets the role's, less its stand-in limits; it ends by itself. Ended covers stay as the history.
const STATUS = {
  RUNNING: { label: 'Running', color: '#16a34a', bg: 'rgba(22,163,74,0.08)' },
  UPCOMING: { label: 'Starts later', color: '#011ff3', bg: 'rgba(1,31,243,0.06)' },
  ENDED: { label: 'Ended', color: '#94a3b8', bg: '#f5f7fa' },
  ENDED_EARLY: { label: 'Ended early', color: '#94a3b8', bg: '#f5f7fa' },
};

const fmt = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const StandInCovers = () => {
  const [data, setData] = useState({ covers: [], roles: [], today: '' });
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ role: 'CMO', stand_in: '', starts_on: '', ends_on: '', note: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await roleCoversAPI.list();
      setData(res.data);
      setForm((f) => ({ ...f, starts_on: f.starts_on || res.data.today }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load stand-ins.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    roleCoversAPI.candidates(form.role).then((res) => setPeople(res.data || [])).catch(() => setPeople([]));
  }, [form.role]);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await roleCoversAPI.create({ ...form, stand_in: parseInt(form.stand_in, 10) });
      setForm((f) => ({ ...f, stand_in: '', ends_on: '', note: '' }));
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to start the cover.');
    } finally {
      setBusy(false);
    }
  };

  const end = async (c) => {
    if (!window.confirm(`End ${c.stand_in_name}'s cover of the ${c.role_label} now?`)) return;
    try { await roleCoversAPI.end(c.id); load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Failed to end the cover.'); }
  };

  const named = people.filter((p) => p.named_stand_in);
  const others = people.filter((p) => !p.named_stand_in);
  const ready = form.stand_in && form.starts_on && form.ends_on;

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
        <FiUserCheck size={18} color="#011ff3" /> Stand-ins
      </h2>
      <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14, maxWidth: 680 }}>
        Let someone cover the <b>CMO</b> or <b>CSM</b> for set dates. They keep their own screens and also get the
        role's, with its Home panel, reminders and KPIs, and it ends by itself. While covering the CMO they can't add
        campaigns, change spend, change weekly series, connect TikTok, delete plan items or build another month's plan.
        Everything they change is stamped in the Audit Log.
      </p>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} /><span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      <div className="cv-subsection" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: '#666', fontSize: 13 }}>Cover the</span>
        <select className="filter-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, stand_in: '' })}>
          {data.roles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
        <span style={{ color: '#666', fontSize: 13 }}>with</span>
        <select className="filter-select" value={form.stand_in} onChange={(e) => setForm({ ...form, stand_in: e.target.value })} style={{ minWidth: 220 }}>
          <option value="">Choose a team member…</option>
          {named.length > 0 && (
            <optgroup label="Named stand-in (Team & Rules)">
              {named.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role_label}</option>)}
            </optgroup>
          )}
          <optgroup label="Everyone else">
            {others.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role_label}</option>)}
          </optgroup>
        </select>
        <span style={{ color: '#666', fontSize: 13 }}>from</span>
        <input type="date" className="filter-select" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} />
        <span style={{ color: '#666', fontSize: 13 }}>to</span>
        <input type="date" className="filter-select" value={form.ends_on} min={form.starts_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} />
        <input type="text" className="filter-select" placeholder="Note (optional)" value={form.note} maxLength={255}
          onChange={(e) => setForm({ ...form, note: e.target.value })} style={{ minWidth: 180 }} />
        <button className="btn-primary" onClick={start} disabled={!ready || busy} style={{ minWidth: 'auto' }}>
          {busy ? 'Starting…' : 'Start cover'}
        </button>
      </div>

      <div className="students-table-container">
        {data.covers.length === 0 ? (
          <div className="empty-state">No stand-ins yet.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>{['Stand-in', 'Covers', 'Dates', 'Status', 'Granted by', ''].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.covers.map((c) => {
                const st = STATUS[c.status] || STATUS.ENDED;
                return (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.stand_in_name}</strong>
                      <div style={{ color: '#94a3b8', fontSize: 11 }}>{c.stand_in_role}</div>
                    </td>
                    <td>{c.role_label}{c.note && <div style={{ color: '#94a3b8', fontSize: 11 }}>{c.note}</div>}</td>
                    <td>
                      {fmt(c.starts_on)} – {fmt(c.ends_on)}
                      {c.ended_early_on && <div style={{ color: '#94a3b8', fontSize: 11 }}>Ended {fmt(c.ended_early_on)} by {c.ended_early_by_name}</div>}
                    </td>
                    <td><span className="status-pill" style={{ color: st.color, background: st.bg }}>{st.label}</span></td>
                    <td>{c.granted_by_name}</td>
                    <td>
                      {(c.status === 'RUNNING' || c.status === 'UPCOMING') && (
                        <button className="btn-secondary" onClick={() => end(c)} style={{ minWidth: 'auto', padding: '4px 10px', fontSize: 12 }}>End now</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StandInCovers;
